import { itemFieldsFromCatalog, matchCatalogToken, normalizeToken } from '../catalog/catalog.js';
import type { CatalogEntry, Channel, Item } from '../model/schema.js';

/**
 * Quick entry line (architecture §3):
 * `<ch#>? <name words> <catalog/model token>? (@<group>)? (48V|+48)? (->|>)<patch>? (#tag)*`
 * e.g. `5 Snare Top SM57 @drums 48V ->SB1:5 #tag`. Options after the name may appear in any order.
 */

/** Semantic content of a quick-entry line. */
export interface QuickEntryValue {
  number?: number;
  name: string;
  /** Catalog ref of the recognised model token. */
  modelRef?: string;
  group?: string;
  /** Only `true` is expressible; absent means "not requested". */
  phantom?: true;
  patch?: string;
  tags: string[];
}

export type QuickTokenKind = 'number' | 'name' | 'model' | 'group' | 'phantom' | 'patch' | 'tag';

/** A token with its character range `[start, end)` in the line (for highlighting). */
export interface QuickToken {
  kind: QuickTokenKind;
  text: string;
  start: number;
  end: number;
}

export type QuickDiagnosticCode =
  | 'number-invalid'
  | 'group-empty'
  | 'group-duplicate'
  | 'phantom-duplicate'
  | 'patch-empty'
  | 'patch-duplicate'
  | 'tag-empty'
  | 'name-missing';

export interface QuickDiagnostic {
  severity: 'error' | 'warning';
  code: QuickDiagnosticCode;
  /** i18n key `quickentry.<code>`. */
  messageKey: string;
  start: number;
  end: number;
}

/** Channel fields produced by a line (no id; number only if given). */
export type ChannelDraft = Omit<Channel, 'id' | 'number'> & { number?: number };

export interface ParseResult {
  value: QuickEntryValue;
  tokens: QuickToken[];
  diagnostics: QuickDiagnostic[];
  channel: ChannelDraft;
  /** Present when a catalog model was recognised: create it with `itemFromCatalog(entry, overrides)`. */
  item?: { entry: CatalogEntry; overrides: Partial<Item> };
}

const PHANTOM = /^(?:\+48v?|48v)$/i;
const ERROR_CODES: ReadonlySet<QuickDiagnosticCode> = new Set([
  'number-invalid',
  'group-empty',
  'patch-empty',
]);

interface RawToken {
  text: string;
  start: number;
  end: number;
}

function tokenize(line: string): RawToken[] {
  return Array.from(line.matchAll(/\S+/g), (m) => ({
    text: m[0],
    start: m.index,
    end: m.index + m[0].length,
  }));
}

function patchRest(text: string): string | undefined {
  if (text.startsWith('->')) return text.slice(2);
  if (text.startsWith('>')) return text.slice(1);
  return undefined;
}

class Parser {
  readonly tokens: QuickToken[] = [];
  readonly diagnostics: QuickDiagnostic[] = [];
  readonly value: QuickEntryValue = { name: '', tags: [] };
  readonly words: RawToken[] = [];

  diag(code: QuickDiagnosticCode, start: number, end: number): void {
    const severity = ERROR_CODES.has(code) ? 'error' : 'warning';
    this.diagnostics.push({ severity, code, messageKey: `quickentry.${code}`, start, end });
  }

  token(kind: QuickTokenKind, t: RawToken): void {
    this.tokens.push({ kind, text: t.text, start: t.start, end: t.end });
  }

  number(t: RawToken): void {
    this.token('number', t);
    const n = Number(t.text);
    if (Number.isSafeInteger(n) && n > 0) this.value.number = n;
    else this.diag('number-invalid', t.start, t.end);
  }

  group(t: RawToken): void {
    this.token('group', t);
    const group = t.text.slice(1);
    if (group === '') this.diag('group-empty', t.start, t.end);
    else if (this.value.group !== undefined) this.diag('group-duplicate', t.start, t.end);
    else this.value.group = group;
  }

  phantom(t: RawToken): void {
    this.token('phantom', t);
    if (this.value.phantom === true) this.diag('phantom-duplicate', t.start, t.end);
    this.value.phantom = true;
  }

  /** Returns how many extra tokens were consumed (the target of a bare `->`). */
  patch(t: RawToken, rest: string, next: RawToken | undefined): number {
    let consumed = 0;
    let target = rest;
    let end = t.end;
    if (rest === '' && next !== undefined) {
      target = next.text;
      end = next.end;
      consumed = 1;
    }
    this.tokens.push({
      kind: 'patch',
      text: consumed === 1 ? `${t.text} ${target}` : t.text,
      start: t.start,
      end,
    });
    if (target === '') this.diag('patch-empty', t.start, end);
    else if (this.value.patch !== undefined) this.diag('patch-duplicate', t.start, end);
    else this.value.patch = target;
    return consumed;
  }

  tag(t: RawToken): void {
    this.token('tag', t);
    const tag = t.text.slice(1);
    if (tag === '') this.diag('tag-empty', t.start, t.end);
    else if (!this.value.tags.includes(tag)) this.value.tags.push(tag);
  }

  /** Splits the plain words into name + trailing model token (one word, else two words). */
  nameAndModel(entries: readonly CatalogEntry[]): CatalogEntry | undefined {
    const w = this.words;
    const last = w[w.length - 1];
    // two words first: "Beta 58" is a Beta 58A, not "Beta" + SM58 (OIL-147)
    const prev = w[w.length - 2];
    let entry =
      prev === undefined || last === undefined
        ? undefined
        : matchCatalogToken(entries, prev.text + last.text);
    let modelWords = entry === undefined ? 0 : 2;
    if (entry === undefined && last !== undefined) {
      entry = matchCatalogToken(entries, last.text);
      modelWords = entry === undefined ? 0 : 1;
    }
    const nameWords = w.slice(0, w.length - modelWords);
    for (const t of nameWords) this.token('name', t);
    if (entry !== undefined) {
      const first = w[w.length - modelWords]!;
      this.tokens.push({
        kind: 'model',
        text: w
          .slice(w.length - modelWords)
          .map((t) => t.text)
          .join(' '),
        start: first.start,
        end: last!.end,
      });
      this.value.modelRef = entry.ref;
    }
    this.value.name = nameWords.map((t) => t.text).join(' ');
    return entry;
  }
}

/** Parses one quick-entry line. Total: never throws; problems are reported as diagnostics. */
export function parseQuickEntry(line: string, entries: readonly CatalogEntry[]): ParseResult {
  const p = new Parser();
  const raw = tokenize(line);
  for (let i = 0; i < raw.length; i++) {
    const t = raw[i]!;
    const rest = patchRest(t.text);
    if (i === 0 && /^\d+$/.test(t.text)) p.number(t);
    else if (t.text.startsWith('@')) p.group(t);
    else if (PHANTOM.test(t.text)) p.phantom(t);
    else if (rest !== undefined) i += p.patch(t, rest, raw[i + 1]);
    else if (t.text.startsWith('#')) p.tag(t);
    else p.words.push(t);
  }
  const entry = p.nameAndModel(entries);
  const v = p.value;
  if (v.name === '' && entry === undefined) p.diag('name-missing', 0, line.length);
  p.tokens.sort((a, b) => a.start - b.start);
  const channel: ChannelDraft = { name: v.name || (entry?.model ?? '') };
  if (v.number !== undefined) channel.number = v.number;
  if (v.phantom === true) channel.phantom = true;
  if (v.group !== undefined) channel.group = v.group;
  if (v.patch !== undefined) channel.patch = v.patch;
  const result: ParseResult = { value: v, tokens: p.tokens, diagnostics: p.diagnostics, channel };
  if (entry !== undefined) {
    const overrides: Partial<Item> = { label: channel.name, tags: [...v.tags] };
    if (v.group !== undefined) overrides.group = v.group;
    result.item = { entry, overrides };
  }
  return result;
}

/** The single token written for a catalog entry: first alias, else the normalised model. */
export function modelToken(entry: CatalogEntry): string {
  return entry.aliases[0] ?? normalizeToken(entry.model);
}

/**
 * Formats a value as a quick-entry line; `parseQuickEntry(formatQuickEntry(v))` yields `v` again
 * for values whose words are unambiguous.
 */
export function formatQuickEntry(value: QuickEntryValue, entries: readonly CatalogEntry[]): string {
  const parts: string[] = [];
  if (value.number !== undefined) parts.push(String(value.number));
  if (value.name !== '') parts.push(value.name);
  const entry =
    value.modelRef === undefined ? undefined : entries.find((e) => e.ref === value.modelRef);
  if (entry !== undefined) parts.push(modelToken(entry));
  if (value.group !== undefined) parts.push(`@${value.group}`);
  if (value.phantom === true) parts.push('48V');
  if (value.patch !== undefined) parts.push(`->${value.patch}`);
  for (const tag of value.tags) parts.push(`#${tag}`);
  return parts.join(' ');
}

/** Builds the quick-entry value of an existing channel (and its source item). */
export function quickEntryValueOf(channel: Channel, item?: Item): QuickEntryValue {
  const value: QuickEntryValue = {
    number: channel.number,
    name: channel.name,
    tags: [...(item?.tags ?? [])],
  };
  if (item?.catalogRef !== undefined) value.modelRef = item.catalogRef;
  if (channel.group !== undefined) value.group = channel.group;
  if (channel.phantom === true) value.phantom = true;
  if (channel.patch !== undefined && channel.patch !== '') value.patch = channel.patch;
  return value;
}

/** Item fields for a parsed line without creating an id (pure; see `itemFromCatalog`). */
export function itemDraftOf(result: ParseResult): Partial<Item> | undefined {
  if (result.item === undefined) return undefined;
  return {
    kind: result.item.entry.kind,
    ...itemFieldsFromCatalog(result.item.entry),
    ...result.item.overrides,
  };
}
